import { apiRequest } from '../../api/client';
import { VND } from '../../format';

const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => (await promise).data;

export type RoundingMode = 'Floor' | 'Ceiling' | 'HalfUp';

/**
 * Nội dung một bản quy tắc — khớp `EarningRules` bên backend.
 *
 * ⚠️ **Prototype `a-earning` vẽ khác mô hình này ở bốn chỗ.** Theo `CLAUDE.md`, prototype
 * quyết hình thức còn business rule quyết hành vi, nên chỗ nào lệch thì rule thắng:
 *
 * 1. Prototype dùng **phần trăm hoàn trên hóa đơn**; BR-101 và `11-config-seed.md` dùng
 *    *số tiền đổi 1 điểm* + *hệ số theo hạng*. Hai cách biểu diễn khác nhau, và con số
 *    ví dụ trong prototype ("bill 1 triệu → 10 điểm") không khớp bộ số seed (ra 100 điểm).
 * 2. Prototype cho **hóa đơn tối thiểu riêng từng hạng**; BR-102 chỉ có một ngưỡng chung.
 * 3. Prototype đặt **trần hóa đơn mỗi nhân viên một giờ** và **hạn hóa đơn hợp lệ** ở đây;
 *    hai thứ đó là BR-203/BR-205, thuộc `system_configs` (tầng 2) chứ không có phiên bản
 *    — xem `13-cau-hinh-va-tinh-diem.md`. Đặt chung vào đây là chụp lại một giá trị mà
 *    giao dịch cũ không cần nhớ.
 * 4. Prototype cho phạm vi **"chỉ một chi nhánh"**; backend chỉ có toàn hệ thống hoặc
 *    theo chuỗi. Thêm phạm vi chi nhánh cần đổi lược đồ, chưa có task.
 */
export interface EarningRules {
  /** Bao nhiêu đồng đổi được 1 điểm. Seed: 10.000đ. */
  vndPerPoint: number;
  /** Dưới mức này không tích điểm (BR-102). Seed: 50.000đ. */
  minBillAmount: number;
  rounding: RoundingMode;
  /** Trần điểm mỗi khách mỗi ngày (BR-104), áp SAU hệ số. Seed: 2.000. */
  dailyEarnCap: number;
  /** Hệ số theo mã hạng (`bronze`/`gold`). Hạng không khai dùng 1,0. */
  tierMultipliers: Record<string, number>;
}

export interface EarningRuleVersion {
  id: string;
  version: string;
  chainId: string | null;
  chainName: string | null;
  rules: EarningRules;
  effectiveFrom: string;
  isPublished: boolean;
  /** Đã phát hành VÀ đã tới ngày hiệu lực. */
  isCurrentlyEffective: boolean;
  note: string | null;
  createdAt: string;
  retiredAt: string | null;
}

export interface EarningPreview {
  billAmount: number;
  basePoints: number;
  tierMultiplier: number;
  campaignMultiplier: number;
  spendablePoints: number;
  tierPoints: number;
  belowMinimumBill: boolean;
  cappedByDailyLimit: boolean;
  pointsLostToCap: number;
  ruleVersion: string;
  explanation: string;
}

export const earningKeys = {
  versions: (chainId?: string) => ['earning-rules', chainId ?? 'all'] as const,
  preview: (amount: number) => ['earning-preview', amount] as const,
};

export const earningApi = {
  list: (chainId?: string) =>
    unwrap(
      apiRequest<EarningRuleVersion[]>(
        `/admin/loyalty/earning-rules${chainId ? `?chainId=${chainId}` : ''}`,
      ),
    ),

  createDraft: (body: {
    version: string;
    chainId: string | null;
    rules: EarningRules;
    effectiveFrom: string;
    note: string | null;
  }) =>
    unwrap(
      apiRequest<EarningRuleVersion>('/admin/loyalty/earning-rules', {
        method: 'POST',
        body,
      }),
    ),

  updateDraft: (
    id: string,
    body: { rules: EarningRules; effectiveFrom: string; note: string | null },
  ) =>
    unwrap(
      apiRequest<EarningRuleVersion>(`/admin/loyalty/earning-rules/${id}`, {
        method: 'PUT',
        body,
      }),
    ),

  publish: (id: string) =>
    unwrap(
      apiRequest<EarningRuleVersion>(`/admin/loyalty/earning-rules/${id}/publish`, {
        method: 'POST',
      }),
    ),

  /** Ngừng bản riêng chuỗi đang áp. Mặc định toàn hệ thống không ngừng được. */
  retire: (id: string) =>
    unwrap(
      apiRequest<EarningRuleVersion>(`/admin/loyalty/earning-rules/${id}/retire`, {
        method: 'POST',
      }),
    ),

  /** Thử tính theo bản ĐANG ÁP DỤNG. Server tính, client không tự nhân chia. */
  preview: (billAmount: number) =>
    unwrap(apiRequest<EarningPreview>(`/loyalty/preview?billAmount=${billAmount}`)),
};

export const ROUNDING_LABELS: Record<RoundingMode, string> = {
  Floor: 'Làm tròn xuống',
  HalfUp: 'Làm tròn gần nhất',
  Ceiling: 'Làm tròn lên',
};

/**
 * Ba bậc theo chính sách PL ban hành 2026-09-11 — mã khớp `TierLevel.Code` /
 * `member_balances.tier_code`. Engine tra mã; tên chỉ để hiện trên form. Bản gieo trước 16/08
 * khoá tên hiển thị nên mọi khách âm thầm nhận hệ số 1,0.
 *
 * ⚠️ Danh sách này chỉ để **vẽ form nhập hệ số**. Bản quy tắc gieo từ 2026-09-11 để
 * `TierMultipliers` RỖNG vì hạng thẻ không nhân hệ số; ai muốn bật lại thì nhập qua form này và
 * phát hành bản mới, không sửa vào seed.
 */
export const SEED_TIER_LEVELS = [
  { code: 'silver', name: 'Silver' },
  { code: 'gold', name: 'Gold' },
  { code: 'diamond', name: 'Diamond' },
] as const;

/**
 * Đọc hệ số một hạng. Ưu tiên mã (`gold`), rồi tên cũ (`Vàng`). Không khớp thì 1,0 —
 * cùng quy tắc với `EarningRules.MultiplierFor` phía server.
 */
export function multiplierFor(
  multipliers: Record<string, number>,
  code: string,
  displayName: string,
): number {
  if (multipliers[code] !== undefined) return multipliers[code];
  if (multipliers[displayName] !== undefined) return multipliers[displayName];

  const wanted = new Set([code.toLowerCase(), displayName.toLowerCase()]);
  const key = Object.keys(multipliers).find((k) => wanted.has(k.toLowerCase()));
  return key !== undefined ? multipliers[key] : 1;
}

export function tierDisplayName(key: string): string {
  const found = SEED_TIER_LEVELS.find(
    (t) => t.code === key || t.name === key || t.code === key.toLowerCase(),
  );
  return found?.name ?? key;
}

/**
 * Trạng thái một bản, diễn đạt như người vận hành nghĩ.
 *
 * Ba trạng thái chứ không phải hai: một bản đã phát hành nhưng ngày hiệu lực còn ở tương
 * lai thì chưa áp dụng — gộp nó vào "đang áp dụng" là nói sai với người đang chờ nó.
 */
export function describeVersionState(
  version: EarningRuleVersion,
  allVersions: EarningRuleVersion[],
): { label: string; tone: 'success' | 'default' | 'warning' } {
  // 'warning' (cam/hổ phách) chứ không 'processing' (xanh dương) — khớp chấm màu "đang soạn"
  // trong prototype (a-earning) và cùng ngôn ngữ màu với banner "bản nháp" (type="warning")
  // đã dùng ở chỗ khác trong trang này và TiersPage.
  if (!version.isPublished) return { label: 'Đang soạn', tone: 'warning' };

  if (version.retiredAt) return { label: 'Đã ngừng', tone: 'default' };

  if (!version.isCurrentlyEffective) return { label: 'Chờ tới ngày hiệu lực', tone: 'warning' };

  // Trong cùng phạm vi, bản đang áp dụng là bản có ngày hiệu lực MUỘN NHẤT mà đã tới hạn
  const sameScope = allVersions.filter(
    (v) => v.chainId === version.chainId && v.isCurrentlyEffective,
  );
  const newest = sameScope.reduce<EarningRuleVersion | null>(
    (best, v) => (best === null || v.effectiveFrom > best.effectiveFrom ? v : best),
    null,
  );

  return newest?.id === version.id
    ? { label: 'Đang áp dụng', tone: 'success' }
    : { label: 'Đã thay thế', tone: 'default' };
}

/** Chỉ bản riêng chuỗi đang áp. Mặc định toàn hệ thống không ngừng được. */
export function canRetireVersion(
  version: EarningRuleVersion,
  allVersions: EarningRuleVersion[],
): boolean {
  return (
    version.chainId != null &&
    !version.retiredAt &&
    describeVersionState(version, allVersions).label === 'Đang áp dụng'
  );
}

/** Một dòng khác biệt giữa hai bản, để hiện bảng so sánh. */
export interface RuleDiffRow {
  label: string;
  before: string;
  after: string;
}


/**
 * So hai bản quy tắc.
 *
 * Chỉ trả về những dòng THỰC SỰ khác. Liệt kê cả những dòng giống nhau thì người đọc
 * phải tự lọc, và đó đúng là lúc bỏ sót thay đổi quan trọng.
 */
export function diffRules(before: EarningRules, after: EarningRules): RuleDiffRow[] {
  const rows: RuleDiffRow[] = [];

  const compare = (label: string, a: string, b: string) => {
    if (a !== b) rows.push({ label, before: a, after: b });
  };

  compare('Số tiền đổi 1 điểm', `${VND.format(before.vndPerPoint)}đ`, `${VND.format(after.vndPerPoint)}đ`);
  compare('Hóa đơn tối thiểu', `${VND.format(before.minBillAmount)}đ`, `${VND.format(after.minBillAmount)}đ`);
  compare('Trần điểm mỗi ngày', `${VND.format(before.dailyEarnCap)} điểm`, `${VND.format(after.dailyEarnCap)} điểm`);
  compare('Cách làm tròn', ROUNDING_LABELS[before.rounding], ROUNDING_LABELS[after.rounding]);

  // Hợp hai tập khóa: hạng bị XÓA khỏi bản mới cũng là một thay đổi, và nó nguy hiểm hơn
  // hạng được thêm — khách hạng đó rơi về hệ số cơ bản mà không ai khai gì cả.
  const tiers = [
    ...new Set([...Object.keys(before.tierMultipliers), ...Object.keys(after.tierMultipliers)]),
  ];
  for (const tier of tiers) {
    compare(
      `Hệ số hạng ${tierDisplayName(tier)}`,
      before.tierMultipliers[tier] === undefined ? 'chưa khai' : `×${before.tierMultipliers[tier]}`,
      after.tierMultipliers[tier] === undefined ? 'chưa khai' : `×${after.tierMultipliers[tier]}`,
    );
  }

  return rows;
}
